import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import Link from 'next/link';
import { ArrowLeft, Calendar, User, Clock, Linkedin, Twitter, Facebook, Instagram, Youtube, Globe } from 'lucide-react';

const BlogDetail = () => {
    const router = useRouter();
    const { slug } = router.query;
    const [blog, setBlog] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!slug) return;

        const fetchBlog = async () => {
            try {
                const res = await fetch(`/projectbim/api/blogs/post/${slug}`);
                if (res.ok) {
                    const data = await res.json();
                    setBlog(data);
                } else {
                    console.error("Blog not found");
                }
            } catch (err) {
                console.error("Error fetching blog:", err);
            } finally {
                setLoading(false);
            }
        };

        fetchBlog();
    }, [slug]);

    if (loading) {
        return (
            <div style={{ backgroundColor: 'var(--color-black)', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-primary)' }}>
                <h2>Loading Article...</h2>
            </div>
        );
    }

    if (!blog) {
        return (
            <div style={{ backgroundColor: 'var(--color-black)', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>
                <h1>Blog Not Found</h1>
                <Link href="/blog" style={{ color: 'var(--color-primary)', marginTop: '20px' }}>Return to Blogs</Link>
            </div>
        );
    }

    // Parse blocks
    let blocks = [];
    try {
        if (blog.content.startsWith('[') || blog.content.startsWith('{')) {
            blocks = JSON.parse(blog.content);
        } else {
            // Legacy support for plain text blogs
            blocks = [{ id: 1, type: 'text', value: blog.content }];
        }
    } catch (e) {
        blocks = [{ id: 1, type: 'text', value: blog.content }];
    }

    const getSocialIcon = (platform) => {
        switch (platform) {
            case 'linkedin': return <Linkedin size={18} />;
            case 'twitter': return <Twitter size={18} />;
            case 'facebook': return <Facebook size={18} />;
            case 'instagram': return <Instagram size={18} />;
            case 'youtube': return <Youtube size={18} />;
            default: return <Globe size={18} />;
        }
    };

    const getSocialColor = (platform) => {
        switch (platform) {
            case 'linkedin': return '#0077b5';
            case 'twitter': return '#1DA1F2';
            case 'facebook': return '#1877F2';
            case 'instagram': return '#E4405F';
            case 'youtube': return '#FF0000';
            default: return 'var(--color-primary)';
        }
    };

    return (
        <div style={{ backgroundColor: 'var(--color-black)', minHeight: '100vh', color: '#fff', fontFamily: 'var(--font-body)' }}>

            <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '60px 40px', display: 'flex', gap: '60px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                <main style={{ flex: '1 1 65%', minWidth: '300px' }}>
                    {/* Back Button */}
                    <Link href="/blog" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ffffff', textDecoration: 'none', marginBottom: '40px', fontSize: '14px', transition: 'color 0.3s' }}>
                        <ArrowLeft size={16} /> Back to all blogs
                    </Link>

                    {/* Header */}
                    <header style={{ marginBottom: '50px' }}>
                        <div style={{ color: 'var(--color-primary)', fontWeight: 'bold', fontSize: '14px', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '15px' }}>
                            {blog.subtitle || 'BIM Insights'}
                        </div>
                        <h1 style={{ fontSize: 'clamp(32px, 5vw, 56px)', lineHeight: '1.1', margin: '0 0 30px 0', fontFamily: 'var(--font-heading)', fontWeight: 'bold' }}>
                            {blog.title}
                        </h1>

                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '25px', color: '#ffffff', fontSize: '14px', borderTop: '1px solid #222', paddingTop: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Calendar size={14} color="var(--color-primary)" /> {new Date(blog.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <User size={14} color="var(--color-primary)" /> By {blog.author || 'ProjectBIM Admin'}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Clock size={14} color="var(--color-primary)" /> {Math.ceil(blog.content.length / 200) || 5} min read
                            </div>
                        </div>
                    </header>

                    {/* Featured Image */}
                    <div style={{ width: '100%', borderRadius: '24px', overflow: 'hidden', marginBottom: '60px', boxShadow: '0 20px 40px rgba(0,0,0,0.4)', maxHeight: '450px', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111' }}>
                        <img src={blog.image_url ? (blog.image_url.startsWith('http') || blog.image_url.startsWith('data:') ? blog.image_url : (blog.image_url.startsWith('/projectbim/uploads/') ? blog.image_url.replace('/projectbim/uploads/', '/projectbim/api/uploads/') : (blog.image_url.startsWith('/uploads/') ? '/projectbim/api' + blog.image_url : (blog.image_url.startsWith('/projectbim/api/uploads/') ? blog.image_url : blog.image_url)))) : ''} alt={blog.title} style={{ width: '100%', height: '100%', maxHeight: '450px', objectFit: 'cover', display: 'block' }} />
                    </div>

                    {/* Dynamic Content Blocks */}
                    <article style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
                        {blocks.map((block) => {
                            return (
                                <div key={block.id}>
                                    {block.type === 'heading' && (
                                        <h2 style={{ fontSize: '32px', color: '#fff', fontFamily: 'var(--font-heading)', marginTop: '20px', marginBottom: '10px' }}>
                                            {block.value}
                                        </h2>
                                    )}

                                    {block.type === 'text' && (
                                        <p style={{ fontSize: '19px', lineHeight: '1.8', color: '#ffffff', margin: 0 }}>
                                            {block.value}
                                        </p>
                                    )}

                                    {block.type === 'image' && block.value && (
                                        <div style={{ margin: '20px 0' }}>
                                            <img src={block.value.startsWith('http') || block.value.startsWith('data:') ? block.value : (block.value.startsWith('/uploads/') ? '/projectbim/api' + block.value : (block.value.startsWith('/projectbim/uploads/') ? block.value.replace('/projectbim/uploads/', '/projectbim/api/uploads/') : block.value))} alt="" style={{ width: '100%', borderRadius: '15px', border: '1px solid #333' }} />
                                        </div>
                                    )}

                                    {block.type === 'list' && block.value && (
                                        <ul style={{ paddingLeft: '20px', margin: '10px 0', display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                            {block.value.split('\n').map((item, i) => item.trim() ? (
                                                <li key={i} style={{ fontSize: '19px', lineHeight: '1.6', color: '#ffffff', paddingLeft: '10px' }}>
                                                    {item}
                                                </li>
                                            ) : null)}
                                        </ul>
                                    )}

                                    {block.type === 'table' && block.value && (
                                        <div style={{ overflowX: 'auto', margin: '30px 0' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', border: '1px solid #333' }}>
                                                <tbody>
                                                    {(() => {
                                                        try {
                                                            const parsedTable = JSON.parse(block.value);
                                                            return Array.isArray(parsedTable) && parsedTable.map((row, rowIndex) => (
                                                                <tr key={rowIndex} style={{ borderBottom: '1px solid #333', backgroundColor: rowIndex === 0 ? 'rgba(84, 178, 72, 0.1)' : 'transparent' }}>
                                                                    {Array.isArray(row) && row.map((cell, cellIndex) => (
                                                                        <td key={cellIndex} style={{ padding: '15px', borderRight: '1px solid #333', color: rowIndex === 0 ? 'var(--color-primary)' : '#ffffff', fontWeight: rowIndex === 0 ? 'bold' : 'normal' }}>
                                                                            {cell}
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            ));
                                                        } catch (e) {
                                                            return <tr><td style={{ color: 'red', padding: '10px' }}>Invalid table format</td></tr>;
                                                        }
                                                    })()}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}

                                    {block.type === 'cta' && block.value && (
                                        <div style={{ margin: '40px 0', padding: '30px', backgroundColor: '#111', borderLeft: '4px solid var(--color-primary)', borderRadius: '0 12px 12px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
                                            <div style={{ fontSize: '20px', fontWeight: 'bold', color: '#fff', flex: '1 1 300px' }}>
                                                {block.value}
                                            </div>
                                            <Link
                                                href="/contactus"
                                                style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '12px 25px', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold', transition: 'all 0.3s ease' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#4aa13e'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(84, 178, 72, 0.4)'; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-primary)'; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
                                            >
                                                Contact Us
                                            </Link>
                                        </div>
                                    )}

                                    {block.type === 'link_button' && block.value && (() => {
                                        let val = { text: '', link: '', align: 'center' };
                                        try { val = JSON.parse(block.value); } catch (e) { }
                                        return (
                                            <div style={{ margin: '30px 0', textAlign: val.align }}>
                                                <Link
                                                    href={val.link || '#'}
                                                    style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '15px 35px', borderRadius: '10px', textDecoration: 'none', fontWeight: 'bold', display: 'inline-block', fontSize: '16px', transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275)', boxShadow: '0 10px 25px rgba(84, 178, 72, 0.2)' }}
                                                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 5px 15px rgba(84, 178, 72, 0.4)'; e.currentTarget.style.backgroundColor = '#4aa13e'; }}
                                                    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 10px 25px rgba(84, 178, 72, 0.2)'; e.currentTarget.style.backgroundColor = 'var(--color-primary)'; }}
                                                >
                                                    {val.text || 'Click Here'}
                                                </Link>
                                            </div>
                                        );
                                    })()}

                                    {block.type === 'image_banner' && block.value && (
                                        <div style={{ margin: '30px 0' }}>
                                            {(() => {
                                                let val = { image_url: '', link: '' };
                                                try { val = JSON.parse(block.value); } catch (e) { }
                                                if (!val.image_url) return null;

                                                const imgElement = (
                                                    <img src={val.image_url.startsWith('http') || val.image_url.startsWith('data:') ? val.image_url : (val.image_url.startsWith('/uploads/') ? '/projectbim/api' + val.image_url : (val.image_url.startsWith('/projectbim/uploads/') ? val.image_url.replace('/projectbim/uploads/', '/projectbim/api/uploads/') : val.image_url))} alt="Banner" style={{ width: '100%', height: 'auto', borderRadius: '15px', display: 'block' }} />
                                                );

                                                if (val.link) {
                                                    return <Link href={val.link} style={{ display: 'block', transition: 'opacity 0.3s' }} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.9'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>{imgElement}</Link>;
                                                }
                                                return imgElement;
                                            })()}
                                        </div>
                                    )}

                                    {block.type === 'video' && block.value && (
                                        <div style={{ margin: '40px 0', aspectRatio: '16/9', width: '100%', borderRadius: '15px', overflow: 'hidden', border: '1px solid #222', boxShadow: '0 20px 40px rgba(0,0,0,0.3)' }}>
                                            {(() => {
                                                const getYouTubeEmbedUrl = (url) => {
                                                    if (!url) return '';
                                                    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
                                                    const match = url.match(regExp);
                                                    return (match && match[2].length === 11) ? `https://www.youtube.com/embed/${match[2]}` : null;
                                                };
                                                const embedUrl = getYouTubeEmbedUrl(block.value);
                                                return embedUrl ? (
                                                    <iframe width="100%" height="100%" src={embedUrl} title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                                                ) : <div style={{ padding: '20px', backgroundColor: '#111', color: '#666', textAlign: 'center', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Invalid Video URL</div>;
                                            })()}
                                        </div>
                                    )}

                                    {block.type === 'quote' && block.value && (
                                        <blockquote style={{ borderLeft: '4px solid var(--color-primary)', padding: '30px 40px', margin: '50px 0', backgroundColor: 'rgba(84, 178, 72, 0.05)', borderRadius: '0 20px 20px 0', fontStyle: 'italic', color: '#fff', fontSize: '24px', lineHeight: '1.6', fontFamily: 'serif', position: 'relative' }}>
                                            <span style={{ position: 'absolute', top: '10px', left: '15px', fontSize: '60px', color: 'rgba(84, 178, 72, 0.2)', fontFamily: 'serif', lineHeight: 1 }}>"</span>
                                            {block.value}
                                        </blockquote>
                                    )}

                                    {block.type === 'divider' && (() => {
                                        let config = { gap: 30, width: 90 };
                                        try {
                                            const parsed = JSON.parse(block.value);
                                            config = typeof parsed === 'object' ? parsed : { gap: parseInt(block.value) || 30, width: 90 };
                                        } catch (e) { config = { gap: parseInt(block.value) || 30, width: 90 }; }

                                        return (
                                            <div style={{ padding: `${config.gap}px 0`, display: 'flex', justifyContent: 'center' }}>
                                                <div style={{ height: '1px', width: `${config.width}%`, background: 'linear-gradient(to right, transparent, var(--color-primary) 20%, var(--color-primary) 80%, transparent)', boxShadow: '0 0 10px rgba(84, 178, 72, 0.2)' }}></div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            );
                        })}
                    </article>

                    {/* Article Footer */}
                    <div style={{ marginTop: '100px', padding: '60px 40px', borderRadius: '30px', background: 'linear-gradient(145deg, #111, #050505)', border: '1px solid #222', textAlign: 'center' }}>
                        <h3 style={{ fontSize: '28px', marginBottom: '15px', fontFamily: 'var(--font-heading)' }}>Ready to transform your project?</h3>
                        <p style={{ color: '#ffffff', marginBottom: '30px', fontSize: '16px' }}>Our BIM experts are here to help you implement these strategies effectively.</p>
                        <Link href="/contactus" style={{ backgroundColor: 'var(--color-primary)', color: '#fff', padding: '16px 35px', borderRadius: '12px', textDecoration: 'none', fontWeight: 'bold', display: 'inline-block', transition: 'transform 0.3s' }}>
                            Contact Our Experts
                        </Link>
                    </div>
                </main>

                {/* Right Sticky Sidebar */}
                <aside style={{ flex: '1 1 30%', minWidth: '300px', position: 'sticky', top: '100px', display: 'flex', flexDirection: 'column', gap: '40px' }}>

                    {/* Author Profile Card */}
                    <div style={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '30px', textAlign: 'center' }}>
                        <div style={{ width: '100px', height: '100px', borderRadius: '50%', overflow: 'hidden', margin: '0 auto 20px auto', border: '3px solid var(--color-primary)' }}>
                            <img src={blog.author_image ? (blog.author_image.startsWith('http') || blog.author_image.startsWith('data:') ? blog.author_image : (blog.author_image.startsWith('/uploads/') ? '/projectbim/api' + blog.author_image : (blog.author_image.startsWith('/projectbim/uploads/') ? blog.author_image.replace('/projectbim/uploads/', '/projectbim/api/uploads/') : blog.author_image))) : 'https://via.placeholder.com/150'} alt={blog.author || 'Author'} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                        <h3 style={{ margin: '0 0 5px 0', color: '#fff', fontSize: '20px' }}>{blog.author || 'ProjectBIM Team'}</h3>
                        <p style={{ color: 'var(--color-primary)', fontSize: '14px', fontWeight: 'bold', margin: '0 0 15px 0' }}>{blog.author_role || 'BIM Expert'}</p>
                        {blog.author_bio && (
                            <p style={{ color: '#ffffff', fontSize: '14px', lineHeight: '1.6', margin: '0 0 20px 0' }}>{blog.author_bio}</p>
                        )}

                        {(() => {
                            const socials = typeof blog.author_socials === 'string' ? JSON.parse(blog.author_socials || '[]') : (blog.author_socials || []);
                            if (Array.isArray(socials) && socials.length > 0) {
                                return (
                                    <div style={{ display: 'flex', justifyContent: 'center', gap: '15px', marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #222' }}>
                                        {socials.map((social, index) => (
                                            <a
                                                key={index}
                                                href={social.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{ color: '#fff', backgroundColor: '#222', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.3s ease' }}
                                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = getSocialColor(social.platform); e.currentTarget.style.transform = 'translateY(-3px)' }}
                                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#222'; e.currentTarget.style.transform = 'translateY(0)' }}
                                            >
                                                {getSocialIcon(social.platform)}
                                            </a>
                                        ))}
                                    </div>
                                );
                            }
                            return null;
                        })()}
                    </div>

                    {/* Categories / Tags Placeholder */}
                    <div style={{ backgroundColor: '#111', border: '1px solid #222', borderRadius: '16px', padding: '30px' }}>
                        <h4 style={{ color: '#fff', fontSize: '18px', margin: '0 0 20px 0', borderBottom: '1px solid #333', paddingBottom: '10px' }}>Tags</h4>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                            {(blog.subtitle || 'BIM Services').split(',').map((tag, index) => (
                                <div key={index} style={{ backgroundColor: 'rgba(84, 178, 72, 0.1)', color: 'var(--color-primary)', padding: '8px 16px', borderRadius: '50px', fontSize: '14px', fontWeight: 'bold' }}>
                                    {tag.trim()}
                                </div>
                            ))}
                        </div>
                    </div>

                </aside>
            </div>
        </div>
    );
};

export default BlogDetail;
